import Box from 'components/shared/Box'
import Loader from 'components/shared/Loader'
import Price from 'components/shared/Price'
import Text from 'components/shared/Text'
import { formatPrice } from 'helpers/helpers'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { CartSummaryDto } from 'store/api-slice/hooks'
import { useCart } from 'store/cart-slice/hooks'
import { useAppSelector } from 'store/hooks'

interface VoucherPaymentContainerProps
    extends Pick<CartSummaryDto, 'totalPrice' | 'beforeDiscountPrice'> {
    voucherValue?: number
    deliveryPrice?: number
}

export const VoucherPaymentContainer = ({
    totalPrice,
    voucherValue,
    beforeDiscountPrice,
    deliveryPrice,
}: VoucherPaymentContainerProps) => {
    const { isCartFetching } = useCart()
    const { items, cart } = useAppSelector((state) => state.cart)
    const { isAssignGuestsVisible } = useAppSelector(
        (state) => state.accommodation
    )
    const { remainingCardValue } = cart || {}
    const isShippingInCart = items.shipping?.productType === 'shipping'

    const orderPrice = useMemo(() => {
        const total = totalPrice ?? 0
        const delivery = isShippingInCart ? deliveryPrice ?? 0 : 0
        return total - delivery || 0
    }, [totalPrice, isShippingInCart, deliveryPrice])

    return (
        <Box gap={[12, 8]}>
            {isShippingInCart && !!deliveryPrice && voucherValue === undefined && (
                <Box
                    direction="row"
                    justify="space-between"
                    align="flex-end"
                    isFullWidth
                >
                    <Text variant="body" color="grayscale_900">
                        <FormattedMessage
                            id="vouchers.payment.order_cost_label"
                            defaultMessage="Koszt zamówienia:"
                        />
                    </Text>
                    {isCartFetching ? (
                        <Loader />
                    ) : (
                        <Box direction="row" align="center" gap={4}>
                            <Price
                                size="heading4"
                                value={orderPrice}
                                color="grayscale_900"
                            />
                            {!!beforeDiscountPrice && (
                                <Price
                                    value={beforeDiscountPrice}
                                    size="body"
                                    color="grayscale_700"
                                    crossedOutVariant="gray"
                                />
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {voucherValue !== undefined && (
                <Box
                    direction="row"
                    justify="space-between"
                    align="flex-end"
                    isFullWidth
                >
                    <Text variant="body" color="grayscale_900">
                        <FormattedMessage
                            id="vouchers.payment.order_cost_label"
                            defaultMessage="Koszt zamówienia:"
                        />
                    </Text>
                    {isCartFetching ? (
                        <Loader />
                    ) : (
                        <Box direction="row" align="center" gap={4}>
                            <Price
                                size="heading4"
                                value={orderPrice}
                                color="grayscale_900"
                            />
                            {!!beforeDiscountPrice && (
                                <Price
                                    value={beforeDiscountPrice}
                                    size="body"
                                    color="grayscale_700"
                                    crossedOutVariant="gray"
                                />
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {isShippingInCart && !!deliveryPrice && (
                <Box
                    direction="row"
                    justify="space-between"
                    align="flex-end"
                    isFullWidth
                >
                    <Text variant="body" color="grayscale_900">
                        <FormattedMessage
                            id="vouchers.payment.delivery_cost_label"
                            defaultMessage="Koszt wysyłki:"
                        />
                    </Text>
                    {isCartFetching ? (
                        <Loader />
                    ) : (
                        <Price
                            size="heading4"
                            value={deliveryPrice}
                            color="grayscale_900"
                        />
                    )}
                </Box>
            )}
            {!!voucherValue && (
                <Box isFullWidth>
                    <Box
                        direction="row"
                        justify="space-between"
                        align="flex-end"
                        isFullWidth
                    >
                        <Text variant="body" color="grayscale_900">
                            <FormattedMessage
                                id="vouchers.payment.delivery_cost_label_voucher"
                                defaultMessage="Z vouchera:"
                            />
                        </Text>
                        {isCartFetching ? (
                            <Loader />
                        ) : (
                            <Price
                                size="heading4"
                                isNegative
                                value={voucherValue || 0}
                                color="grayscale_900"
                            />
                        )}
                    </Box>
                    {remainingCardValue ? (
                        <Text variant="bodySmall" color="grayscale_700">
                            {!isAssignGuestsVisible ? (
                                <FormattedMessage
                                    id="vouchers.payment.delivery_cost_label_voucher_remaining"
                                    defaultMessage="Na voucherze pozostanie <price></price>"
                                    values={{
                                        price: () => (
                                            <>
                                                <Text
                                                    as="span"
                                                    variant="bodySmall"
                                                    color="grayscale_700"
                                                >
                                                    {formatPrice(
                                                        remainingCardValue,
                                                        true
                                                    )}
                                                </Text>
                                            </>
                                        ),
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="vouchers.payment.delivery_cost_label_voucher_accomodation"
                                    defaultMessage="Voucher nie obejmuje noclegu. Na voucherze pozostanie <price></price>"
                                    values={{
                                        price: () => (
                                            <>
                                                <Text
                                                    as="span"
                                                    variant="bodySmall"
                                                    color="grayscale_700"
                                                >
                                                    {formatPrice(
                                                        remainingCardValue,
                                                        true
                                                    )}
                                                </Text>
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        </Text>
                    ) : null}
                </Box>
            )}
        </Box>
    )
}
